import { useEffect, useRef, useState } from 'react'

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import {
  CloseOutlined as CloseIcon,
  CloseFullscreenOutlined as DockIcon,
  OpenInFullOutlined as UndockIcon,
  FileDownloadOutlined as DownloadIcon,
  DragIndicatorOutlined as DragIcon,
} from '@mui/icons-material'

import { darkGrey } from '$constants'

import { useDimensions } from '$hooks'

import { PdfViewer } from './PdfViewer'
import { ImageViewer } from './ImageViewer'
import { FileViewerInternalData } from './FileViewerContainer'
import { LoadedLoadingBar } from '$components/common'
import { downloadFile } from '$utils'

type FileViewerProps = {
  data: FileViewerInternalData | null
  isDocked: boolean
  setIsFileVisible: (newValue: boolean) => void
  setIsDocked?: (newValue: boolean) => void
  dragHandleClass: string
  showHeader?: boolean
  showDockControls?: boolean
  cardSxProps?: SxProps<Theme>
  extraPdfViewerUrlParams?: string
}

/**
 * Main UI for the file viewer, rendered when the viewer has been opened.
 */
const FileViewer = ({
  data,
  isDocked,
  setIsFileVisible,
  setIsDocked,
  dragHandleClass,
  showHeader = true,
  showDockControls = true,
  cardSxProps = {},
  extraPdfViewerUrlParams = '',
}: FileViewerProps) => {
  const [cardDimensions, setCardDimensions] = useState({ width: 0, height: 0 })

  const cardRef = useRef(null)

  const headerRef = useRef(null)
  const { height: headerHeight } = useDimensions(headerRef)

  const theme = useTheme()
  const { contentTop, pageBottomGap } = theme.loaded.hostLayout
  const spacing = parseInt(theme.spacing(1).replace('px', ''))

  // Docked it fills the page height, undocked half the window.
  const viewerHeight = isDocked
    ? `calc(100vh - ${contentTop} - ${pageBottomGap})`
    : 'calc(100vh * 0.5)'

  // Add a listener for the resize event on the card, this fires when it first
  // renders, and then if the user resizes it. We take the width and height
  // information from this and pump it through calculations to determine the
  // right size for rendering the file content. Note that the object component
  // needs explicit width/height values so we can't just use CSS styling rules.
  useEffect(() => {
    const elRef = cardRef.current

    const resizeObserver = new ResizeObserver((entries) => {
      const { width, height } = entries[0]?.contentRect ?? {}
      if (
        width &&
        height &&
        width !== cardDimensions.width &&
        height !== cardDimensions.height
      ) {
        setCardDimensions({ width, height })
      }
    })

    elRef && resizeObserver.observe(elRef)

    return () => {
      if (elRef) {
        resizeObserver.unobserve(elRef)
      } else {
        resizeObserver.disconnect()
      }
    }
  }, [cardRef, cardDimensions, setCardDimensions])

  const undockViewer = () => {
    scrollTo(0, 0)
    setIsDocked && setIsDocked(false)
  }

  const dockViewer = () => {
    setIsDocked && setIsDocked(true)
  }

  const closeFile = () => {
    // when closing the viewer we set it's state to be docked again, otherwise
    // if the user closed the viewer while it was undocked, the next time they
    // open it, it will be immediately undocked
    setIsDocked && setIsDocked(true)
    setIsFileVisible(false)
  }

  // Trigger a download for the file content. We use this instead of a direct
  // anchor tag so that the hover state on the link doesn't have a really ugly
  // (and meaningless to the user) data uri shown in the status bar.
  const downloadFileRequested = () => {
    if (!data) return
    downloadFile(data.fileName, data.fileObjectURL)
  }

  return (
    <Card
      ref={cardRef}
      sx={{
        // Sticks where the page content starts.
        position: 'sticky',
        top: contentTop,
        height: viewerHeight,

        // file viewer should take up no more than 1/3 the screen, minus
        // section margins
        minWidth: `calc((100vw / 3) - ${theme.loaded.gutterSize * 3}px - ${
          theme.loaded.gutterSize
        }px)`,

        // no wider than an ipad, arbritrary width but reasonable in usage
        maxWidth: isDocked ? '768px' : '100%',

        // space between columns, we can't use flex-gap on the parent as it
        // will be shown even when the file viewer is hidden, in effect
        // causing it to look like there is a double gutter between the nav
        // and the page content
        marginRight: `${theme.loaded.gutterSize}px`,

        // no default padding, we apply this to the content instead
        padding: 0,

        // when undocked we need a drop shadow to so the viewer appears to float
        boxShadow: isDocked ? 'none' : theme.loaded.boxShadow,

        // when undocked we allow the user to resize the viewer
        resize: isDocked ? 'none' : 'both',

        // Allow these props to be overridden if necessary.
        ...cardSxProps,
      }}
    >
      {showHeader && (
        <CardHeader
          ref={headerRef}
          title={
            <Stack direction={'row'}>
              {!isDocked && (
                <>
                  <DragIcon sx={{ color: darkGrey }} />
                  <Divider orientation={'vertical'} flexItem />
                </>
              )}
              <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                {data?.fileName}
              </Typography>
            </Stack>
          }
          action={
            <Stack direction={'row'}>
              <Divider orientation={'vertical'} flexItem />
              {data && (
                <>
                  <DownloadIcon
                    onClick={downloadFileRequested}
                    sx={{ color: darkGrey, marginRight: 2, cursor: 'pointer' }}
                  />
                  <Divider orientation={'vertical'} flexItem />
                </>
              )}
              {showDockControls && (
                <>
                  {isDocked ? (
                    <UndockIcon
                      onClick={undockViewer}
                      sx={{
                        color: darkGrey,
                        marginRight: 2,
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    <DockIcon
                      onClick={dockViewer}
                      sx={{
                        color: darkGrey,
                        marginRight: 2,
                        cursor: 'pointer',
                      }}
                    />
                  )}
                  <Divider orientation={'vertical'} flexItem />
                </>
              )}
              <CloseIcon
                onClick={closeFile}
                sx={{ color: darkGrey, cursor: 'pointer' }}
              />
            </Stack>
          }
          sx={{
            '& .MuiCardHeader-root': {
              padding: 0,
            },
            ...(!isDocked && { cursor: 'grab' }),
          }}
          className={dragHandleClass}
        />
      )}
      <CardContent
        sx={{
          paddingTop: 0,
          paddingBottom: '0 !important', // required to overpower a last child base theme rule
        }}
      >
        {data && data.contentType === 'application/pdf' && (
          <PdfViewer
            contentType={data.contentType}
            fileUri={data.fileObjectURL}
            width={cardDimensions.width - spacing * 4}
            height={
              cardDimensions.height - spacing * 4 - (headerHeight + spacing * 2)
            }
            extraPdfViewerUrlParams={extraPdfViewerUrlParams}
          />
        )}
        {data && data.contentType.startsWith('image/') && (
          <ImageViewer
            fileUri={data.fileObjectURL}
            width={cardDimensions.width - spacing * 4}
            height={
              cardDimensions.height - spacing * 4 - (headerHeight + spacing * 2)
            }
          />
        )}
        {!data && (
          <Box
            display={'flex'}
            justifyContent={'center'}
            alignItems={'center'}
            width={cardDimensions.width - spacing * 4}
            height={
              cardDimensions.height - spacing * 4 - (headerHeight + spacing * 2)
            }
          >
            <LoadedLoadingBar />
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
FileViewer.displayName = 'FileViewer'

export default FileViewer
